import { muiTheme } from "@/styles/theme";
import { AgentTargetDTO, MonthTargetDTO } from "../dtos/index.v2";
import { TargetFull } from "../types";

import { useMemo } from "react";

// Helper to sort targets by from, then to
function sortTargets(targets: MonthTargetDTO[]) {
  return [...targets].sort((a, b) => {
    const fromA = new Date(a.from).getTime();
    const fromB = new Date(b.from).getTime();
    if (fromA !== fromB) return fromA - fromB;
    const toA = new Date(a.to).getTime();
    const toB = new Date(b.to).getTime();
    return toA - toB;
  });
}

export const useAgentTargetsMapping = ({
  agentTarget,
}: {
  agentTarget: AgentTargetDTO;
}) => {
  // Always use sorted targets
  const sortedTargets = useMemo(
    () => sortTargets(agentTarget.targets),
    [agentTarget.targets]
  );

  const totalTarget = useMemo(
    () => calculateTotalTarget({ ...agentTarget, targets: sortedTargets }),
    [agentTarget, sortedTargets]
  );

  const monthlyTargetsAll = useMemo(
    () => sortedTargets.map((target) => mapMonthlyTarget(target)),
    [sortedTargets]
  );

  const monthlyTargets = useMemo(
    () => ({
      personal: monthlyTargetsAll.map((target) => target.personal),
      team: monthlyTargetsAll.map((target) => target.team),
    }),
    [monthlyTargetsAll]
  );

  return { totalTarget, monthlyTargets };
};

export function getProgressColor(progress: number) {
  if (progress > 0.7) {
    return muiTheme.colorSchemes.light.palette.success[700];
  } else if (progress > 0.5 && progress <= 0.7) {
    return muiTheme.colorSchemes.light.palette.success[400];
  } else if (progress > 0.3 && progress <= 0.5) {
    return muiTheme.colorSchemes.light.palette.warning[500];
  } else {
    return muiTheme.colorSchemes.light.palette.error[400];
  }
}

export function mapMonthlyTarget(monthlyTarget: MonthTargetDTO): TargetFull {
  const personalTarget = monthlyTarget.personalTarget;
  const teamTarget = monthlyTarget.teamTarget;
  const personalAchievment = monthlyTarget.personalAchievement;
  const teamAchievment = monthlyTarget.teamAchievement;
  const targetAdjust = monthlyTarget.targetAdjust;
  const extraAchievment = monthlyTarget.extraAchievement;

  return {
    personal: {
      achieved: personalAchievment ?? 0,
      target: personalTarget,
      extraAchieved: extraAchievment,
      targetAdjustment: targetAdjust,
      progressPercentage: personalTarget
        ? (personalAchievment ?? 0) / personalTarget
        : 0,
      progressColor: getProgressColor(personalAchievment / personalTarget),
    },
    team: {
      achieved: teamAchievment ?? 0,
      target: teamTarget,
      extraAchieved: extraAchievment,
      targetAdjustment: targetAdjust,
      progressPercentage: teamTarget ? (teamAchievment ?? 0) / teamTarget : 0,
      progressColor: getProgressColor(teamAchievment / teamTarget),
    },
  };
}

export function calculateTotalTarget(agentTarget: AgentTargetDTO): TargetFull {
  // Always use sorted targets for aggregation
  const sortedTargets = sortTargets(agentTarget.targets);

  const personalTarget = sortedTargets.reduce(
    (acc, target) => acc + (target.personalTarget ?? 0),
    0
  );
  const teamTarget = sortedTargets.reduce(
    (acc, target) => acc + target.teamTarget,
    0
  );

  const personalAchievment = sortedTargets.reduce(
    (acc, target) => acc + (target.personalAchievement ?? 0),
    0
  );

  const teamAchievment = sortedTargets.reduce(
    (acc, target) => acc + (target.teamAchievement ?? 0),
    0
  );

  const targetAdjust = sortedTargets.reduce(
    (acc, target) => acc + (target.targetAdjust ?? 0),
    0
  );

  const extraAchievment = sortedTargets.reduce(
    (acc, target) => acc + (target.extraAchievement ?? 0),
    0
  );

  return {
    personal: {
      achieved: personalAchievment,
      target: personalTarget,
      extraAchieved: extraAchievment,
      targetAdjustment: targetAdjust,
      progressPercentage: personalTarget
        ? personalAchievment / personalTarget
        : 0,
      progressColor: getProgressColor(personalAchievment / personalTarget),
    },
    team: {
      achieved: teamAchievment ?? 0,
      target: teamTarget,
      extraAchieved: extraAchievment,
      targetAdjustment: targetAdjust,
      progressPercentage: teamTarget ? teamAchievment / teamTarget : 0,
      progressColor: getProgressColor(teamAchievment / teamTarget),
    },
  };
}
